import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { HollowButton, IconButton, SolidButton } from '../../components/buttons-visuals';
import { ContentContainer } from '../../components/containers';
import ConfirmModal from '../../components/confirmModal';
import { ClassSessionService } from '../../services/classSessionService';

const QuizWhizzerCatalog = (): JSX.Element => {
    const [games, setGames] = useState<Core.Models.QuizWhizzerCatalogGame[]>([]);
    const [topics, setTopics] = useState<Core.Models.QuizWhizzerTopic[]>([]);
    const [selectedGameId, setSelectedGameId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSeeding, setIsSeeding] = useState(false);
    const [csvText, setCsvText] = useState('');
    const [topicToDelete, setTopicToDelete] = useState<Core.Models.QuizWhizzerTopic | undefined>();
    const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
    const [isDeletingAll, setIsDeletingAll] = useState(false);

    const loadGamesAndTopics = useCallback(async (preferredGameId?: string) => {
        setIsLoading(true);
        try {
            const gameList = await ClassSessionService.getCatalogGames();
            setGames(gameList);
            const gameId =
                (preferredGameId && gameList.some((g) => g.id === preferredGameId) ? preferredGameId : undefined) ||
                gameList[0]?.id;
            if (gameId) {
                setSelectedGameId(gameId);
                setTopics(await ClassSessionService.getTopics(gameId));
            } else {
                setSelectedGameId('');
                setTopics([]);
            }
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadGamesAndTopics();
    }, [loadGamesAndTopics]);

    const selectedGame = useMemo(() => games.find((g) => g.id === selectedGameId), [games, selectedGameId]);

    const subjects = useMemo(() => {
        const set = new Set(topics.map((t) => t.subject));
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [topics]);

    const seedCatalog = async () => {
        setIsSeeding(true);
        try {
            await ClassSessionService.ensureCatalog();
            toast.success('Catalog initialized and topics seeded.');
            await loadGamesAndTopics();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsSeeding(false);
        }
    };

    const onGameChange = async (gameId: string) => {
        setSelectedGameId(gameId);
        try {
            setTopics(await ClassSessionService.getTopics(gameId));
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        }
    };

    const importCsv = async () => {
        if (!selectedGameId || !csvText.trim()) {
            toast.error('Select a game and paste CSV content first.');
            return;
        }
        try {
            const added = await ClassSessionService.importTopics(selectedGameId, csvText);
            toast.success(`Imported ${added} new topic(s).`);
            setCsvText('');
            setTopics(await ClassSessionService.getTopics(selectedGameId));
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        }
    };

    const confirmDeleteTopic = async () => {
        if (!topicToDelete) return;
        try {
            await ClassSessionService.deleteTopic(topicToDelete.id);
            setTopics((prev) => prev.filter((t) => t.id !== topicToDelete.id));
            setTopicToDelete(undefined);
            toast.success('Topic deleted.');
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        }
    };

    const confirmDeleteAllTopics = async () => {
        if (!selectedGameId) return;
        setIsDeletingAll(true);
        try {
            const deleted = await ClassSessionService.deleteAllTopics(selectedGameId);
            setTopics([]);
            setConfirmDeleteAll(false);
            toast.success(`Deleted ${deleted} topic(s).`);
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsDeletingAll(false);
        }
    };

    const gameImageUrl = selectedGame?.cardUrl || selectedGame?.iconUrl;

    return (
        <ContentContainer shade={Core.Models.Shades.Dark40}>
            <h3 className="heading-3">Showcase Catalog</h3>
            <p className="mb2x">
                Manage topics and launch links for showcase games. Seed creates AcroAura and Dueling Definitions;
                import/update topics for a selected game. Art comes from the game record. New games are created via the
                normal Add Game flow, then appear here for CSV import.
            </p>

            <div className="disp-flex flex-gap mb2x">
                <SolidButton as="button" onClick={seedCatalog} pending={isSeeding} size="small">
                    Initialize / seed catalog
                </SolidButton>
                <HollowButton
                    as="button"
                    onClick={() => void loadGamesAndTopics(selectedGameId)}
                    size="small"
                    disabled={isLoading}
                >
                    Refresh
                </HollowButton>
            </div>

            {games.length > 0 && (
                <label className="disp-block mb2x">
                    Game
                    <select
                        className="full-width"
                        value={selectedGameId}
                        onChange={(e) => onGameChange(e.target.value)}
                    >
                        {games.map((g) => (
                            <option key={g.id} value={g.id}>
                                {g.name}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            {selectedGame && (
                <>
                    <div className="disp-flex align-center justify-between flex-gap flex-wrap mb2x">
                        <div className="disp-flex align-center flex-gap">
                            {gameImageUrl && (
                                <img
                                    src={gameImageUrl}
                                    alt={selectedGame.name}
                                    style={{ width: '4rem', height: '4rem', objectFit: 'cover' }}
                                />
                            )}
                            <p className="mb0">
                                {subjects.length} subjects · {topics.length} topics
                            </p>
                        </div>
                        <HollowButton
                            as="button"
                            color="destructive"
                            size="small"
                            disabled={topics.length === 0 || isDeletingAll}
                            onClick={() => setConfirmDeleteAll(true)}
                        >
                            Delete all topics
                        </HollowButton>
                    </div>

                    <label className="disp-block mb">
                        Import CSV (paste)
                        <textarea
                            className="full-width"
                            rows={4}
                            value={csvText}
                            onChange={(e) => setCsvText(e.target.value)}
                            placeholder="topic_number,subject,subject_slug,theme,topic,is_combined,quiz_id,quiz_title,launch_url"
                        />
                    </label>
                    <SolidButton as="button" className="mb2x" onClick={importCsv} size="small">
                        Import CSV
                    </SolidButton>

                    <div style={{ maxHeight: '40rem', overflow: 'auto' }}>
                        <table className="full-width">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Subject</th>
                                    <th>Topic</th>
                                    <th>Launch</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {topics.map((t) => (
                                    <tr key={t.id}>
                                        <td>{t.topicNumber}</td>
                                        <td>{t.subject}</td>
                                        <td>{t.topic}</td>
                                        <td>
                                            <a href={t.launchUrl} target="_blank" rel="noopener noreferrer">
                                                Open
                                            </a>
                                        </td>
                                        <td>
                                            <IconButton
                                                as="button"
                                                buttonLabel="Delete topic"
                                                buttonSize="small"
                                                color="destructive"
                                                onClick={() => setTopicToDelete(t)}
                                            >
                                                <FontAwesomeIcon icon={['fas', 'trash']} />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {!isLoading && games.length === 0 && (
                <p>No showcase games yet. Click Initialize / seed catalog to create AcroAura and Dueling Definitions.</p>
            )}

            {topicToDelete && (
                <ConfirmModal
                    onCancel={() => setTopicToDelete(undefined)}
                    onConfirm={confirmDeleteTopic}
                    title="Delete topic?"
                >
                    <p>
                        Delete <strong>{topicToDelete.topic}</strong> from the catalog? This cannot be undone.
                    </p>
                </ConfirmModal>
            )}

            {confirmDeleteAll && selectedGame && (
                <ConfirmModal
                    onCancel={() => setConfirmDeleteAll(false)}
                    onConfirm={confirmDeleteAllTopics}
                    title="Delete all topics?"
                >
                    <p>
                        Delete all <strong>{topics.length}</strong> topic(s) for{' '}
                        <strong>{selectedGame.name}</strong>? You can re-seed or import CSV afterward. This cannot be
                        undone.
                    </p>
                </ConfirmModal>
            )}
        </ContentContainer>
    );
};

export default QuizWhizzerCatalog;
